// Two-phase password reset:
//   /auth/password-reset             → request email
//   /auth/password-reset#token=…     → set new password (consumes the link)

import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '../context/AuthContext.js';
import { Button } from '../components/ui/Button.js';
import { auth as authApi } from '../lib/api.js';
import { takeAuthCallbackToken } from '../lib/auth-callback.js';

const inputStyle: React.CSSProperties = {
  backgroundColor: 'var(--ac-surface)',
  borderColor: 'var(--ac-border)',
  color: 'var(--ac-text)',
};

export default function PasswordResetPage() {
  const [, navigate] = useLocation();
  const { setAuth } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    const fragmentToken = takeAuthCallbackToken();
    if (fragmentToken) setToken(fragmentToken);
  }, []);

  // Phase 1: request email
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Phase 2: set new password
  const [pw, setPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  useEffect(() => {
    setErr(null);
  }, [token]);

  const onRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await authApi.requestPasswordReset(email.trim());
      setSent(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!token) return;
    if (pw.length < 12) {
      setErr('Password must be at least 12 characters.');
      return;
    }
    if (pw !== confirmPw) {
      setErr('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const result = await authApi.confirmPasswordReset(token, pw);
      setAuth(result.accessToken, result.user);
      navigate('/');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--ac-bg)' }}
    >
      <div
        className="w-full max-w-md p-6 rounded-2xl border flex flex-col gap-4"
        style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
      >
        <h1 className="text-xl font-semibold" style={{ color: 'var(--ac-text)' }}>
          Reset password
        </h1>

        {token ? (
          <form onSubmit={onConfirm} className="flex flex-col gap-3">
            <p className="text-sm" style={{ color: 'var(--ac-text-muted)' }}>
              Enter a new password. You&apos;ll be signed in automatically.
            </p>
            <input
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="New password (min. 12 characters)"
              className="rounded-lg border px-3 py-2 text-sm"
              style={inputStyle}
            />
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              placeholder="Confirm new password"
              className="rounded-lg border px-3 py-2 text-sm"
              style={inputStyle}
            />
            <Button type="submit" disabled={busy || !pw || !confirmPw}>
              {busy ? 'Setting…' : 'Set new password'}
            </Button>
          </form>
        ) : sent ? (
          <div className="flex flex-col gap-2">
            <p style={{ color: 'var(--ac-text)' }}>
              If an account exists for <strong>{email}</strong>, a reset link has been emailed.
            </p>
            <p className="text-sm" style={{ color: 'var(--ac-text-muted)' }}>
              The link expires in 15 minutes. Check your spam folder if it doesn&apos;t arrive.
            </p>
          </div>
        ) : (
          <form onSubmit={onRequest} className="flex flex-col gap-3">
            <p className="text-sm" style={{ color: 'var(--ac-text-muted)' }}>
              Enter your email and we&apos;ll send you a link to set a new password.
            </p>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="rounded-lg border px-3 py-2 text-sm"
              style={inputStyle}
            />
            <Button type="submit" disabled={busy || !email}>
              {busy ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        )}

        {err && (
          <div className="text-sm" style={{ color: '#e06161' }}>
            {err}
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="text-sm underline self-start"
          style={{ color: 'var(--ac-text-muted)' }}
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
}
